// src/components/CollegaSopralluogo.js
// ═══════════════════════════════════════════════════════════════════════════
// «COLLEGA A COMMESSA» per un sopralluogo fatto prima della commessa (ottobre 2026, W416)
// ═══════════════════════════════════════════════════════════════════════════
// Finestra con le commesse aperte: in cima quelle dello stesso cliente o della Richiesta
// Commerciale da cui è nato il sopralluogo (utils/sopralluoghi.js, commesseProposte).
// Usata dalla pagina «Sopralluoghi senza commessa» e dal Registro Sopralluoghi.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { commesseProposte, collegaSopralluogo, clienteDelSopralluogo } from "../utils/sopralluoghi";

export default function CollegaSopralluogo({ sopralluogo, orgId, onClose, onCollegato }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const [progetti, setProgetti] = useState([]);
  const [rc, setRc] = useState([]);
  const [scelta, setScelta] = useState("");
  const [cerca, setCerca] = useState("");
  const [lavoro, setLavoro] = useState(false);
  useEffect(() => {
    if (!orgId) return undefined;
    const u = [
      onSnapshot(query(collection(db, "projects"), where("organizationId", "==", orgId)), s => setProgetti(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setProgetti([])),
      onSnapshot(query(collection(db, "richiesteCommerciali"), where("orgId", "==", orgId)), s => setRc(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setRc([])),
    ];
    return () => u.forEach(f => f());
  }, [orgId]);
  const perId = useMemo(() => Object.fromEntries(progetti.map(p => [p.id, p])), [progetti]);
  const proposte = useMemo(() => commesseProposte(progetti, sopralluogo, rc), [progetti, sopralluogo, rc]);
  const testo = cerca.trim().toLowerCase();
  const visibili = proposte.filter(p => !testo || `${p.codiceCommessa || ""} ${p.name || ""} ${perId[p.parentId]?.name || ""}`.toLowerCase().includes(testo));
  const consigliate = visibili.filter(p => p.consigliata);
  const altre = visibili.filter(p => !p.consigliata);
  const etichetta = (p) => `${p.codiceCommessa ? `[${p.codiceCommessa}] ` : ""}${p.name || "Commessa"}${perId[p.parentId]?.name ? ` — ${perId[p.parentId].name}` : ""}`;

  const collega = async () => {
    const p = progetti.find(x => x.id === scelta);
    if (!p) return;
    setLavoro(true);
    try {
      await collegaSopralluogo(sopralluogo, p, currentUser || {}, perId);
      addToast(`${sopralluogo.numero || "Sopralluogo"} collegato a ${p.codiceCommessa || p.name} ✅`, "success");
      onCollegato?.(p);
      onClose();
    } catch { addToast("Collegamento non riuscito: riprova", "error"); }
    finally { setLavoro(false); }
  };

  return (
    <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-label="Collega a commessa" style={{ maxWidth: 520, fontFamily: "'Inter',sans-serif" }}>
        <h3 style={{ fontSize: 15.5, fontWeight: 800, color: "#1e1b4b", margin: "0 0 4px" }}>🔗 Collega {sopralluogo.numero || "il sopralluogo"} a una commessa</h3>
        <p style={{ fontSize: 12.5, color: "#6b7280", margin: "0 0 12px", lineHeight: 1.5 }}>
          Cliente: <b>{clienteDelSopralluogo(sopralluogo) || "—"}</b>{sopralluogo.rcNumero ? ` · da ${sopralluogo.rcNumero}` : ""}.
          Collegato, il sopralluogo compare nella scheda Sopralluogo della commessa, nella Cabina di Regia e nel Report, come se fosse nato lì.
        </p>
        <input aria-label="Cerca commessa" className="input" placeholder="Cerca per codice, nome o cliente…" value={cerca} onChange={e => setCerca(e.target.value)} style={{ marginBottom: 8 }} />
        <select aria-label="Commessa" className="input" size={Math.min(8, Math.max(3, visibili.length + 2))} value={scelta} onChange={e => setScelta(e.target.value)} style={{ width: "100%", height: "auto" }}>
          {consigliate.length > 0 && <optgroup label="Stesso cliente o stessa Richiesta Commerciale">
            {consigliate.map(p => <option key={p.id} value={p.id}>{etichetta(p)}</option>)}
          </optgroup>}
          {altre.length > 0 && <optgroup label="Altre commesse aperte">
            {altre.map(p => <option key={p.id} value={p.id}>{etichetta(p)}</option>)}
          </optgroup>}
        </select>
        {!visibili.length && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>Nessuna commessa aperta trovata.</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14, flexWrap: "wrap" }}>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={!scelta || lavoro} onClick={collega}>{lavoro ? "Collego…" : "Collega"}</button>
        </div>
      </div>
    </div>
  );
}
